import { useMemo } from 'react';

import { useAppSelector } from 'store/store-hooks';
import { convertTransition } from 'components/app/_helpers/_app.helper';
import { selectSbInitialization } from 'components/app/_mediator/app.slice';
import { getRangeMarkerTimes } from 'components/app/_mediator/range-markers/range-markers.helper';
import { Error } from 'components/app/_helpers/app-errors.helper';
import { selectStats } from 'components/app/editor/_mediator/editor.slice';

export const useRenderValidation = () => {
  const errorRateMap = useAppSelector((state) => state.sb.errorRateMap);
  const isSbInitialized = useAppSelector(selectSbInitialization);
  const markers = useAppSelector((state) => state.editor.markers);
  const duration = useAppSelector((state) => state.audio.duration);
  const storyConfig = useAppSelector((state) => state.editor.storyConfig);
  const numberOfClips = useAppSelector(selectStats)?.numberOfClips;

  const level1 = useMemo(() => {
    if (!isSbInitialized) return Error.sbIsNotInitialized();
    if (!numberOfClips) return Error.renderWithEmptyClipsMap();
    return;
  }, [numberOfClips, isSbInitialized]);

  const level2 = useMemo(() => {
    if (level1) return level1;
    if (Object.values(errorRateMap).length) return Error.renderWithInvalidPlaybackRate();
    return;
  }, [level1, errorRateMap]);

  const level3 = useMemo(() => {
    if (level2) return level2;
    if (!markers || !storyConfig || !duration) return level2;

    const { finishMarkerId, startMarkerId, creditsDuration, titleDuration, trackFadeInTime, trackFadeOutTime } =
      storyConfig;
    const { startTime, endTime } = getRangeMarkerTimes({
      startMarkerId,
      finishMarkerId,
      duration,
      markers,
    });
    const storyLength = endTime - startTime;
    const textDuration = convertTransition(creditsDuration) + convertTransition(titleDuration);
    const fadeDuration = convertTransition(trackFadeInTime) + convertTransition(trackFadeOutTime);

    if (textDuration > storyLength) return Error.invalidStoryLength('Title', 'Credits', 'duration');
    if (fadeDuration > storyLength) return Error.invalidStoryLength('FadeIn', 'FadeOut', 'time');
  }, [level2, duration, storyConfig]);

  return level3;
};
